import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import _merge from 'lodash/merge';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import { ButtonPrimary, SpacerVertical } from '@klarna/bubble-ui';
import {
  analytics,
  defaultContainerPropTypes,
  FieldsetForObject,
  Input,
  formUtils,
  HorizontalWrapper,
  prependHttpScheme,
  TOOLTIP_MODES,
} from 'moet-core';
import { generateCyElemId, isRunningContextCypress } from 'moet-test-utils/cypress/utils';
import { withResponsive } from 'mage-components';
import {
  email as emailValidator,
  url as urlValidator,
  storeName as storeNameValidator,
} from '@klarna/mf-validations';

import { validators } from '@/flow/validation';
import { AutoSuggest, ContainerTemplate } from '@/components';
import { getSetupData, isKustomPartner } from '@/selectors/data';
import { getSupportedCountries, getGoodsCategories } from '@/selectors/options';
import { hasEmailConfirmationExperimentActive } from '@/selectors/experiments';
import { getMerchantAwareTranslator } from '@/selectors/merchant';
import * as a from '@/actions/signup';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_BASIC_INFO_CONTAINER_IDS } from './constants';

const tPrefix = 'products-fe.containers.basic_info';
const tStoreDetailsPrefix = 'products-fe.containers.store_details';

export class BasicInfo extends React.Component {
  constructor(props) {
    super(props);

    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onNext = this.onNext.bind(this);
    this.prepareFields = this.prepareFields.bind(this);

    const fields = this.prepareFields();

    this.state = {
      fields,
      language: props.language,
      isLoading: false,
      isDisabled: false,
      isEmailFocused: false,
    };
  }

  prepareFields() {
    const {
      countries = [],
      setupData = {},
      t,
      tMerchantAware,
      isEmailConfirmationExperimentActive,
      goodsCategories,
    } = this.props;
    const { fields } = this.state || {};
    const values = formUtils.getMappedValues(fields);
    const {
      firstName = '',
      lastName = '',
      email = '',
      website = '',
      country = '',
      goodsCategory = '',
      storeName = '',
    } = _merge(setupData, values);

    return formUtils.initForm({
      firstName: {
        component: Input,
        size: 1,
        type: 'text',
        tabIndex: 1,
        label: t(`${tPrefix}.fields.first_name.label`),
        value: firstName,
        extraProps: {
          centerOnFocus: 'start',
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_FIRST_NAME, window),
        },
      },
      lastName: {
        component: Input,
        size: 1,
        type: 'text',
        tabIndex: 2,
        label: t(`${tPrefix}.fields.last_name.label`),
        value: lastName,
        extraProps: {
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_LAST_NAME, window),
        },
      },
      email: {
        component: Input,
        size: 1,
        type: 'text',
        tabIndex: 4,
        label: t(`${tPrefix}.fields.email.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.email.errors.required`),
          },
          {
            callback: emailValidator.validate,
            errorMessage: t(`${tPrefix}.fields.email.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: email,
        extraProps: {
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_EMAIL, window),
        },
      },
      ...(isEmailConfirmationExperimentActive
        ? {
            emailConfirmation: {
              component: Input,
              size: 1,
              type: 'text',
              label: t(`${tPrefix}.fields.email_confirmation.label`),
              validators: [
                {
                  callback: validators.common.isRequired,
                  errorMessage: t(`${tPrefix}.fields.email_confirmation.errors.required`),
                },
                {
                  callback: (value, { email }) => value === email,
                  errorMessage: t(`${tPrefix}.fields.email_confirmation.errors.invalid`),
                  stateSelector: state => ({ email: state?.fields?.email?.value }),
                },
              ],
              isInitialized: false,
              isValid: false,
              value: email,
              extraProps: {
                tooltipMode: TOOLTIP_MODES.HELPER,
                tooltipText: t(`${tPrefix}.fields.email_confirmation.tooltip`),
                ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_EMAIL_CONFIRMATION, window),
              },
            },
          }
        : {}),
      website: {
        component: Input,
        size: 1,
        type: 'text',
        tabIndex: 5,
        label: t(`${tPrefix}.fields.website.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.website.errors.required`),
          },
          {
            callback: urlValidator.validate,
            errorMessage: t(`${tPrefix}.fields.website.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: website,
        extraProps: {
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_WEBSITE, window),
        },
      },
      country: {
        component: isRunningContextCypress(window) ? Input : AutoSuggest,
        size: 1,
        tabIndex: 6,
        label: t(`${tPrefix}.fields.country.label`),
        value: country,
        isInitialized: false,
        isValid: false,
        validators: [
          {
            callback: validators.common.isOneOf,
            errorMessage: t(`${tPrefix}.fields.country.errors.general`),
            options: { subject: countries.map(({ value }) => value) },
          },
        ],
        extraProps: {
          borderless: true,
          options: countries,
          noEmptyOption: true,
          allowCustomValue: false,
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_COUNTRY, window),
        },
      },
      storeName: {
        component: Input,
        size: 1,
        type: 'text',
        tabIndex: 3,
        label: tMerchantAware(`${tStoreDetailsPrefix}.fields.store_name.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: tMerchantAware(
              `${tStoreDetailsPrefix}.fields.store_name.errors.required`,
            ),
          },
          {
            callback: validators.common.isString({ min: 2 }),
            errorMessage: tMerchantAware(`${tStoreDetailsPrefix}.fields.store_name.errors.invalid`),
          },
          {
            callback: storeNameValidator.validate,
            errorMessage: tMerchantAware(
              `${tStoreDetailsPrefix}.fields.store_name.errors.invalid_characters`,
            ),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storeName,
        extraProps: {
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.INPUT_STORE_NAME, window),
        },
      },
      goodsCategory: {
        component: AutoSuggest,
        size: 1,
        tabIndex: 7,
        label: t(`${tStoreDetailsPrefix}.fields.goods_category.label`),
        validators: [
          {
            callback: validators.common.isOneOf,
            errorMessage: t(`${tStoreDetailsPrefix}.fields.goods_category.errors.invalid`),
            options: { subject: goodsCategories.map(({ value }) => value) },
          },
        ],
        extraProps: {
          borderless: true,
          options: goodsCategories,
          noEmptyOption: true,
          allowCustomValue: false,
          ...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.SELECT_CATEGORY, window),
        },
        isInitialized: false,
        isValid: false,
        value: goodsCategory,
      },
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.language !== this.props.language) {
      const fields = this.prepareFields();
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ fields, language: this.props.language });
    }
  }

  onBlur(key) {
    formUtils.onBlurFields(key, this.state, fields => this.setState({ fields }));

    if (key === 'email') {
      this.setState({ isEmailFocused: false });
    }
  }

  onChange(key, value) {
    formUtils.onChangeFields(key, value, this.state, fields => this.setState({ fields }));
  }

  onFocus(key) {
    if (key === 'email') {
      this.setState({ isEmailFocused: true });
    } else {
      this.setState({ isEmailFocused: false });
    }
  }

  onNext() {
    this.setState({ fields: formUtils.initializeFields(this.state.fields) });

    formUtils.callToActionOnNextWrapper(this.state.fields, () => {
      const {
        firstName,
        lastName,
        email,
        website,
        country,
        storeName,
        goodsCategory,
      } = formUtils.getMappedValues(this.state.fields);
      const leadData = {
        firstName,
        lastName,
        email,
        website: prependHttpScheme(website),
        country,
        storeName,
        goodsCategory,
      };

      this.setState({ isLoading: true, isDisabled: true });

      return this.props.onChangeData(leadData).then(() => {
        analytics.addEmailToUser(email);
        analytics.logEvent(ANALYTICS_EVENTS.basicInfo, leadData);

        return this.props.onNext();
      });
    });
  }

  getBody() {
    const {
      firstName,
      lastName,
      email,
      website,
      country,
      storeName,
      goodsCategory,
    } = formUtils.getMappedValues(this.state.fields);

    return {
      firstName,
      lastName,
      email,
      website: prependHttpScheme(website),
      country,
      storeName,
      goodsCategory,
    };
  }

  render() {
    const {
      isDisabled: isPropsisDisabled,
      isLoading: isPropsLoading,
      t,
      isKustomPartner,
    } = this.props;
    const { isLoading: isStateLoading, isDisabled: isStateDisabled } = this.state;

    const isDisabled = isPropsisDisabled || isStateDisabled;
    const isLoading = isPropsLoading || isStateLoading;

    const fields = formUtils.initFormFromState({
      ...this.state,
      fields: {
        ...this.state.fields,
        email: {
          ...this.state.fields.email,
          extraProps: {
            ...this.state.fields.email.extraProps,
          },
        },
      },
    }).fields;

    const brand = isKustomPartner ? 'Kustom' : 'Klarna';

    // Adjust brand name for language-specific correctness (e.g., Czech, Finnish)
    let brandLocalized = brand;
    if (this.props.language === 'cs') {
      brandLocalized = isKustomPartner ? 'Kustomem' : 'Klarnou';
    } else if (this.props.language === 'fi') {
      brandLocalized = isKustomPartner ? 'Kustomin' : 'Klarnan';
    }

    return (
      <>
        <ContainerTemplate
          title={
            t(`${tPrefix}.title_line_1`) +
            ' ' +
            t(`${tPrefix}.title_line_2`, { brand: brandLocalized })
          }
          subtitle={t(`${tPrefix}.subtitle`)}
          showRecaptchaFooter
        >
          <div style={{ zIndex: 1 }}>
            <div
              style={{
                display: 'flex',
                gap: '30px',
              }}
            >
              <div style={{ flex: 1 }}>
                <FieldsetForObject
                  name="basic-info"
                  fields={{ firstName: fields.firstName }}
                  isDisabled={isDisabled}
                  onChange={this.onChange}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                />
                <FieldsetForObject
                  name="basic-info"
                  fields={{ storeName: fields.storeName }}
                  isDisabled={isDisabled}
                  onChange={this.onChange}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                />
              </div>
              <div style={{ flex: 1 }}>
                <FieldsetForObject
                  name="basic-info"
                  fields={{ lastName: fields.lastName }}
                  isDisabled={isDisabled}
                  onChange={this.onChange}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                />
                <FieldsetForObject
                  name="basic-info"
                  fields={{ email: fields.email }}
                  isDisabled={isDisabled}
                  onChange={this.onChange}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                />
              </div>
            </div>
            <FieldsetForObject
              name="basic-info"
              isDisabled={isDisabled}
              fields={{
                website: fields.website,
                country: fields.country,
                goodsCategory: fields.goodsCategory,
              }}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            />
          </div>
          <SpacerVertical spaceToken="space/600" />
          <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
            <ButtonPrimary
              onClick={this.onNext}
              loading={isLoading}
              {...generateCyElemId(CY_BASIC_INFO_CONTAINER_IDS.BTN_CONTINUE, window)}
            >
              {t(`${tPrefix}.buttons.continue`)}
            </ButtonPrimary>
          </HorizontalWrapper>
        </ContainerTemplate>
      </>
    );
  }
}

BasicInfo.propTypes = {
  ...defaultContainerPropTypes,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isEmailConfirmationExperimentActive: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  goodsCategories: PropTypes.array,
  isKustomPartner: PropTypes.bool,
  recaptchaChallengeCompleted: PropTypes.bool,
  recaptchaResponseToken: PropTypes.string,
  setupData: PropTypes.shape({
    country: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    website: PropTypes.string.isRequired,
  }),
};

export default compose(
  withRouter,
  connect(
    state => ({
      countries: getSupportedCountries(state).map(({ key, ...rest }) => ({ value: key, ...rest })),
      goodsCategories: getGoodsCategories(state).map(({ key, ...rest }) => ({
        value: key,
        ...rest,
      })),
      isEmailConfirmationExperimentActive: hasEmailConfirmationExperimentActive(state),
      language: i18nSelectors.language(state),
      setupData: getSetupData(state),
      t: translator(state),
      tMerchantAware: getMerchantAwareTranslator(state),
      isKustomPartner: isKustomPartner(state),
    }),
    dispatch => ({
      onChangeData: ({ firstName, lastName, email, website, country, storeName, goodsCategory }) =>
        new Promise(resolve => {
          a.onChangeFormValue('firstName', firstName)(dispatch);
          a.onChangeFormValue('lastName', lastName)(dispatch);
          a.onChangeFormValue('email', email)(dispatch);
          a.onChangeFormValue('website', website)(dispatch);
          a.onChangeFormValue('country', country)(dispatch);
          a.onChangeFormValue('storeName', storeName)(dispatch);
          a.onChangeFormValue('goodsCategory', goodsCategory)(dispatch);
          resolve();
        }),
    }),
  ),
  withResponsive,
)(BasicInfo);
